import { Alert, App as AntApp, Button, Drawer, Grid, Input, Modal, Popconfirm, Segmented, Space, Table, Tabs, Tag, Typography } from "antd";
import { PlusOutlined, StopOutlined, CheckCircleOutlined, SafetyCertificateOutlined, HistoryOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { roleTagColor } from "../app/roles.ts";
import { requestFailure } from "../app/form-errors.ts";
import { CreateUserDrawer } from "../components/CreateUserDrawer.tsx";
import { EditRolesModal } from "../components/EditRolesModal.tsx";
import { AuditHistory } from "../components/AuditHistory.tsx";
import { SecurityEvents } from "../components/SecurityEvents.tsx";

type UserDetails = components["schemas"]["UserDetails"];
type CreatedUser = components["schemas"]["CreatedUserResponse"];
type ActiveFilter = "all" | "active" | "inactive";

export function UsersPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { user: me, hasPermission } = useAuth();
  const canReadAudit = hasPermission("audit.read");
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ActiveFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserDetails | null>(null);
  const [temporary, setTemporary] = useState<CreatedUser | null>(null);
  const [historyOf, setHistoryOf] = useState<UserDetails | null>(null);

  const query = useQuery({
    queryKey: ["users", { search, filter, page, pageSize }],
    queryFn: async () => {
      const { data, error } = await api.GET("/users", {
        params: {
          query: {
            ...(search ? { Search: search } : {}),
            ...(filter === "all" ? {} : { IsActive: filter === "active" }),
            Page: page,
            PageSize: pageSize,
          },
        },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const toggle = useMutation({
    mutationFn: async (user: UserDetails) => {
      const path = user.isActive ? "/users/{id}/deactivate" : "/users/{id}/reactivate";
      const { data, error, response } = await api.POST(path, { params: { path: { id: user.id } } });
      if (!data) throw requestFailure(error, response.status);
      return data;
    },
    onSuccess: async (user) => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
      void message.success(t(user.isActive ? "users.reactivated" : "users.deactivated", { name: user.displayName }));
    },
    onError: (failure: ReturnType<typeof requestFailure>) => {
      void message.error(failure.problem?.title ?? t("app.error"));
    },
  });

  const columns = [
    {
      title: t("users.columns.user"),
      key: "user",
      ellipsis: true,
      render: (_: unknown, user: UserDetails) => (
        <div style={{ lineHeight: 1.3 }}>
          <Typography.Text strong>{user.displayName}</Typography.Text>
          {user.id === me?.id && (
            <Tag style={{ marginInlineStart: 8 }} variant="filled">
              {t("users.you")}
            </Tag>
          )}
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {user.userName}
            </Typography.Text>
          </div>
        </div>
      ),
    },
    {
      title: t("users.columns.roles"),
      key: "roles",
      render: (_: unknown, user: UserDetails) => (
        <Space size={[4, 4]} wrap>
          {user.roles.map((role) => (
            <Tag key={role} color={roleTagColor[role]} style={{ marginInlineEnd: 0 }}>
              {t(`roles.${role}`)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t("users.columns.contact"),
      key: "contact",
      responsive: ["md" as const],
      render: (_: unknown, user: UserDetails) => (
        <div style={{ lineHeight: 1.4 }}>
          {user.phone && <div>{user.phone}</div>}
          {user.email && <Typography.Text type="secondary">{user.email}</Typography.Text>}
        </div>
      ),
    },
    {
      title: t("users.columns.status"),
      key: "status",
      width: 110,
      render: (_: unknown, user: UserDetails) =>
        user.isActive ? <Tag color="success">{t("users.status.active")}</Tag> : <Tag color="default">{t("users.status.inactive")}</Tag>,
    },
    {
      title: t("users.columns.lastLogin"),
      key: "lastLogin",
      width: 150,
      responsive: ["lg" as const],
      render: (_: unknown, user: UserDetails) =>
        user.lastLoginAt ? dayjs(user.lastLoginAt).format("DD.MM.YYYY HH:mm") : <Typography.Text type="secondary">{t("users.neverLoggedIn")}</Typography.Text>,
    },
    {
      title: t("users.columns.actions"),
      key: "actions",
      width: screens.md ? (canReadAudit ? 320 : 230) : canReadAudit ? 150 : 110,
      render: (_: unknown, user: UserDetails) => {
        const isMe = user.id === me?.id;
        return (
          <Space size={4} wrap>
            {canReadAudit && (
              <Button size="small" icon={<HistoryOutlined />} onClick={() => setHistoryOf(user)} aria-label={`${t("audit.history")}: ${user.displayName}`}>
                {screens.md && t("audit.history")}
              </Button>
            )}
            <Button size="small" icon={<SafetyCertificateOutlined />} disabled={isMe} onClick={() => setEditing(user)} aria-label={`${t("users.editRoles")}: ${user.displayName}`}>
              {screens.md && t("users.editRoles")}
            </Button>
            <Popconfirm
              title={user.isActive ? t("users.deactivate") : t("users.reactivate")}
              description={<div style={{ maxWidth: 320 }}>{t(user.isActive ? "users.deactivateConfirm" : "users.reactivateConfirm", { name: user.displayName })}</div>}
              okText={t("users.confirm")}
              cancelText={t("users.cancel")}
              okButtonProps={{ danger: user.isActive }}
              onConfirm={() => toggle.mutate(user)}
              disabled={isMe}
            >
              <Button
                size="small"
                danger={user.isActive}
                icon={user.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                disabled={isMe}
                loading={toggle.isPending && toggle.variables?.id === user.id}
                aria-label={`${user.isActive ? t("users.deactivate") : t("users.reactivate")}: ${user.displayName}`}
              >
                {screens.md && (user.isActive ? t("users.deactivate") : t("users.reactivate"))}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("users.title")}
        </Typography.Title>
        <Space wrap>
          <Segmented<ActiveFilter>
            value={filter}
            onChange={(value) => {
              setFilter(value);
              setPage(1);
            }}
            options={[
              { value: "all", label: t("users.filter.all") },
              { value: "active", label: t("users.filter.active") },
              { value: "inactive", label: t("users.filter.inactive") },
            ]}
          />
          <Input.Search
            allowClear
            placeholder={t("users.search")}
            aria-label={t("users.search")}
            style={{ width: screens.md ? 320 : 220 }}
            onSearch={(value) => {
              setSearch(value.trim());
              setPage(1);
            }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            {t("users.create")}
          </Button>
        </Space>
      </div>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<UserDetails>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("users.empty") }}
        scroll={{ x: 560 }}
        pagination={{
          current: page,
          pageSize,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: true,
          showTotal: (total) => t("users.total", { count: total }),
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
      <CreateUserDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(result) => {
          setCreating(false);
          if (result.temporaryPassword) setTemporary(result);
          else void message.success(t("users.created", { name: result.user.displayName }));
        }}
      />
      <EditRolesModal
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={(user) => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ["audit"] });
          void message.success(t("users.rolesUpdated", { name: user.displayName }));
        }}
      />
      <Drawer
        title={historyOf ? t("audit.userHistoryTitle", { name: historyOf.displayName }) : t("audit.title")}
        open={historyOf !== null}
        onClose={() => setHistoryOf(null)}
        size={screens.md ? 820 : "100%"}
        destroyOnHidden
      >
        {historyOf && (
          <Tabs
            items={[
              { key: "changes", label: t("audit.tabs.changes"), children: <AuditHistory entityType="User" entityId={historyOf.id} /> },
              { key: "signIns", label: t("audit.tabs.signIns"), children: <SecurityEvents userId={historyOf.id} /> },
            ]}
          />
        )}
      </Drawer>
      <Modal
        title={t("users.temporaryPassword.title")}
        open={temporary !== null}
        onCancel={() => setTemporary(null)}
        onOk={() => setTemporary(null)}
        okText={t("users.temporaryPassword.done")}
        cancelButtonProps={{ style: { display: "none" } }}
        destroyOnHidden
      >
        {temporary && (
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Alert type="warning" showIcon title={t("users.temporaryPassword.intro", { name: temporary.user.displayName })} />
            <div>
              <Typography.Text type="secondary">{t("users.temporaryPassword.login")}</Typography.Text>
              <Typography.Paragraph copyable={{ tooltips: [t("users.temporaryPassword.copy"), t("users.temporaryPassword.copied")] }} style={{ marginBottom: 8 }}>
                <Typography.Text code style={{ fontSize: 16 }}>
                  {temporary.user.userName}
                </Typography.Text>
              </Typography.Paragraph>
              <Typography.Text type="secondary">{t("users.temporaryPassword.password")}</Typography.Text>
              <Typography.Paragraph copyable={{ text: temporary.temporaryPassword ?? "", tooltips: [t("users.temporaryPassword.copy"), t("users.temporaryPassword.copied")] }} style={{ marginBottom: 0 }}>
                <Typography.Text code style={{ fontSize: 18, letterSpacing: 1 }}>
                  {temporary.temporaryPassword}
                </Typography.Text>
              </Typography.Paragraph>
            </div>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
